<script setup>
import { computed, useTemplateRef, defineEmits } from 'vue';
import { useWindowSize, useElementBounding } from '@vueuse/core';

// Zimobi imports
import { isMobileScreenWidth } from 'dashboard/helper/zimobi/responsivenessHelper';
// Zimobi imports

const props = defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  selectedOptionKey: {
    type: String,
    default: '',
  },
  subMenuAvailable: {
    type: Boolean,
    default: true,
  },
});

// Zimobi
const emit = defineEmits(['selectOptionKey']);
// Zimobi

const menuRef = useTemplateRef('menuRef');
const { width: windowWidth, height: windowHeight } = useWindowSize();
const { bottom, right } = useElementBounding(menuRef);

// Vertical position
const verticalPosition = computed(() => {
  const SUBMENU_HEIGHT = 240; // 15rem in pixels
  const spaceBelow = windowHeight.value - bottom.value;
  return spaceBelow < SUBMENU_HEIGHT ? 'bottom-0' : 'top-0';
});

// Horizontal position
const horizontalPosition = computed(() => {
  // Zimobi
  // On mobile the submenu must always open to the right.
  if (isMobileScreenWidth(windowWidth.value)) return 'left-full';
  // Zimobi

  const SUBMENU_WIDTH = 240;
  const spaceRight = windowWidth.value - right.value;
  return spaceRight < SUBMENU_WIDTH ? 'right-full' : 'left-full';
});

const submenuPosition = computed(() => [
  verticalPosition.value,
  horizontalPosition.value,
]);

// Zimobi
const submenuOpen = computed(
  () => props.selectedOptionKey === props.option.key
);

const toggleSubmenu = () => {
  if (isMobileScreenWidth(window.innerWidth)) {
    emit('selectOptionKey', submenuOpen.value ? '' : props.option.key);
  }
};
// Zimobi
</script>

<template>
  <div
    ref="menuRef"
    class="text-n-slate-12 menu-with-submenu min-width-calc w-full p-1 flex items-center h-7 rounded-md relative bg-n-alpha-3/50 backdrop-blur-[100px] justify-between sm:hover:bg-n-brand/10 cursor-pointer sm:dark:hover:bg-n-solid-3"
    :class="[
      !subMenuAvailable ? 'opacity-50 cursor-not-allowed' : '',
      { 'bg-n-brand/10 dark:bg-n-solid-3': submenuOpen },
    ]"
    @click.stop="toggleSubmenu"
  >
    <div class="flex items-center h-4">
      <fluent-icon :icon="option.icon" size="14" class="menu-icon" />
      <p class="my-0 mx-2 text-xs">{{ option.label }}</p>
    </div>
    <fluent-icon icon="chevron-right" size="12" />
    <div
      v-if="subMenuAvailable"
      class="submenu bg-n-alpha-3 backdrop-blur-[100px] p-1 shadow-lg rounded-md absolute hidden max-h-[15rem] overflow-y-auto overflow-x-hidden cursor-pointer"
      :class="[submenuPosition, { '!block': submenuOpen }]"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
.menu-with-submenu {
  @apply min-w-0 sm:min-w-[calc(6.25rem*2)];

  &:hover {
    .submenu {
      @apply sm:block;
    }
  }
}
</style>
